import { z } from 'zod';
import { issue, msg } from '@/lib/forms/messages';
import { codePointLength } from '@/lib/forms/primitives';
import {
  DOCUMENT_ACCESS_LEVELS,
  DOCUMENT_CLASSIFICATIONS,
  type DocumentAccessLevel,
  type DocumentClassification,
} from '@constants/documentMetadata';

// Derived from the same constants the pickers render, so the schema cannot drift from the UI.
const valuesOf = <T extends string>(options: ReadonlyArray<{ value: T }>) =>
  options.map((option) => option.value) as [T, ...T[]];

// Only category/description/tags reach the server (services/document.service.ts:52-60, api/routes/documents.py:209-211); the rest is dropped at the service boundary but stays as form state the dirty guard weighs.
// category `min(1)` is unreachable through the button (`disabled={!formData.category}`,
// DocumentMetadataForm.tsx:69) and kept as the rendering contract for a direct submit —
// pinned by DocumentMetadataForm.characterization.test.tsx:263-272.
// No category enum: api/routes/documents.py:288-294 falls back to 'general' for unknown codes (seeds 008_initial_data.sql:24-32).
// No description cap (TEXT, 004_document_management.sql:37; title VARCHAR(500) at :36 is unused — api/routes/documents.py:424 sets title=file.filename) and no `.trim()`: stored as typed.
export const documentMetadataSchema = z.object({
  category: z.string().min(1, msg('metadata.categoryRequired')),
  // Mirrors tags.name VARCHAR(100) (005_metadata_tagging.sql:11); upload does not truncate
  // (api/routes/documents.py:303 only strips). On the array, not the element: one slot.
  tags: z.array(z.string()).superRefine((tags, ctx) => {
    // Lowercased too: _normalize_name writes name_normalized VARCHAR(100), and lowercasing
    // can expand — 'İ' becomes two code points.
    const tooLong = (tag: string) =>
      codePointLength(tag) > 100 || codePointLength(tag.trim().toLowerCase()) > 100;
    if (tags.some(tooLong)) {
      ctx.addIssue(issue('validation.maxLength', [], { max: 100 }));
    }
  }),
  description: z.string(),
  // Translated department LABEL, '' = none; normalising '' to undefined here would change the payload.
  department: z.string().optional(),
  // These two enums are TYPE derivation, not runtime validation: both are fixed Selects
  // built from the same constants, so neither can fail. Deleting them fails tsc.
  classification: z.enum(valuesOf<DocumentClassification>(DOCUMENT_CLASSIFICATIONS)),
  permissions: z.object({
    defaultAccess: z.enum(valuesOf<DocumentAccessLevel>(DOCUMENT_ACCESS_LEVELS)),
    allowShare: z.boolean(),
    allowDownload: z.boolean(),
  }),
});

export type DocumentMetadataFormValues = z.infer<typeof documentMetadataSchema>;

export const DOCUMENT_METADATA_DEFAULTS: DocumentMetadataFormValues = {
  category: '',
  tags: [],
  description: '',
  department: '',
  classification: 'internal',
  permissions: {
    defaultAccess: 'team',
    allowShare: true,
    allowDownload: true,
  },
};
